import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useUpdateCreatorMutation,
  useUploadUserImageMutation,
  useGetUserCreatorQuery,
  useGetMyCreatorProductCategoriesQuery,
  useCreateCreatorProductCategoryMutation,
  useAddProductToCreatorCategoryMutation,
  useRemoveProductFromCreatorCategoryMutation,
} from "../slices/usersApiSlice";
import {
  useGetMyCreatorProductsQuery,
  useCreateProductCreatorMutation,
} from "../slices/productsApiSlice";
import Loader from "../components/Loader";
import Message from "../components/Message";
import CreatorPaymentWarning from "../components/CreatorPaymentWarning";
import Paginate from "../components/Paginate";
import {
  FaInstagram,
  FaPinterest,
  FaFacebook,
  FaTiktok,
  FaUser,
  FaCheckCircle,
} from "react-icons/fa";
import Product from "../components/Product";
import SearchHelper from "../components/SearchHelper";
import { skipToken } from "@reduxjs/toolkit/query";
import SearchBoxCreator from "../components/SearchBoxCreator";
import { useSelector } from "react-redux";
import { toast } from "sonner";
import { validateIfNeeded } from "../utils/fileValidators";
import { getUploadErrorMessage } from "../utils/uploadToastMessages";

function CreatorEditScreen() {
  const { pageNumber, keyword, age, category, sort } = useParams();
  const navigate = useNavigate();
  const [selectedCreatorCategory, setSelectedCreatorCategory] = useState("all");
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [categoryInputName, setCategoryInputName] = useState("");
  const [categoryModalProductId, setCategoryModalProductId] = useState(null);
  const [isCategoryModalSubmitting, setIsCategoryModalSubmitting] = useState(false);

  const [createProductCreator, { isLoading: isCreating }] = useCreateProductCreatorMutation();
  const { userInfo } = useSelector((state) => state.auth);

  const { data: creatorInfo, isLoading: creatorInfoLoading } = useGetUserCreatorQuery(userInfo._id);
  const { data: creatorCategoryData, refetch: refetchCreatorMeta } =
    useGetMyCreatorProductCategoriesQuery(undefined, {
      skip: !userInfo?._id,
    });

  const creatorCategories = creatorCategoryData?.categories || [];
  const [createCreatorProductCategory] = useCreateCreatorProductCategoryMutation();
  const [addProductToCreatorCategory] = useAddProductToCreatorCategoryMutation();
  const [removeProductFromCreatorCategory] = useRemoveProductFromCreatorCategoryMutation();


  const [name, setName] = useState("");
  const [tag, setTag] = useState("");
  const [description, setDescription] = useState("");
  const [bannerImage, setBannerImage] = useState("");
  const [profileImage, setProfileImage] = useState("");
  const [bannerError, setBannerError] = useState(false);
  const [profileError, setProfileError] = useState(false);
  const [facebook, setFacebook] = useState("");
  const [instagram, setInstagram] = useState("");
  const [pinterest, setPinterest] = useState("");
  const [tiktok, setTiktok] = useState("");
  const [facebookToggle, setFacebookToggle] = useState(false);
  const [instagramToggle, setInstagramToggle] = useState(false);
  const [pinterestToggle, setPinterestToggle] = useState(false);
  const [tiktokToggle, setTiktokToggle] = useState(false);

  useEffect(() => {
    if (creatorInfo) {
      setName(creatorInfo?.name || "");
      setTag(creatorInfo?.tag || "");
      setDescription(creatorInfo?.description || "");
      setBannerImage(creatorInfo?.bannerImage || "");
      setProfileImage(creatorInfo?.profileImage || "");
      setFacebook(creatorInfo?.handlers.facebook.url || "");
      setInstagram(creatorInfo?.handlers.instagram.url || "");
      setPinterest(creatorInfo?.handlers.pinterest.url || "");
      setTiktok(creatorInfo?.handlers.tiktok.url || "");
      setFacebookToggle(creatorInfo?.handlers.facebook.isEnabled || false);
      setInstagramToggle(creatorInfo?.handlers.instagram.isEnabled || false);
      setPinterestToggle(creatorInfo?.handlers.pinterest.isEnabled || false);
      setTiktokToggle(creatorInfo?.handlers.tiktok.isEnabled || false);
    }
  }, [creatorInfo]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [updateCreator, { isLoading: isUpdating, error: notUpdating }] = useUpdateCreatorMutation();
  const [uploadImage] = useUploadUserImageMutation();
  const [bannerUploading, setBannerUploading] = useState(false);
  const bannerStatus = bannerImage && !bannerError ? "Banner uploaded" : "No banner uploaded yet";
  const profileStatus =
    profileImage && !profileError ? "Profile image uploaded" : "No profile image uploaded yet";

  const submitHandler = async (e) => {
    e.preventDefault();
    if (bannerUploading) return;

    const updatedCreatorData = {
      name,
      tag,
      profileImage,
      bannerImage,
      description,
      handlers: {
        facebook: { url: facebook, isEnabled: facebookToggle },
        instagram: { url: instagram, isEnabled: instagramToggle },
        pinterest: { url: pinterest, isEnabled: pinterestToggle },
        tiktok: { url: tiktok, isEnabled: tiktokToggle },
      },
    };

    try {
      await updateCreator(updatedCreatorData).unwrap();
      toast.success("Creator data updated successfully!");
      refetch();
    } catch {
      toast.error("Failed to update creator data");
    }
  };

  const uploadFileHandler = async (e, fieldName) => {
    const file = e.target.files?.[0];
    if (!file || (fieldName === "banner" && bannerUploading)) return;
    if (fieldName === "banner") setBannerUploading(true);

    try {
      await validateIfNeeded(fieldName, [file]);
      const formData = new FormData();
      formData.append(fieldName, file);
      const res = await uploadImage(formData).unwrap();
      toast.success("Upload successful");
      const uploadedPath = res.paths?.[fieldName]?.[0] || "";

      if (fieldName === "banner") {
        setBannerError(false);
        setBannerImage(uploadedPath);
      } else if (fieldName === "profile") {
        setProfileError(false);
        setProfileImage(uploadedPath);
      }
    } catch (err) {
      toast.error(getUploadErrorMessage(err, "File upload failed"));
    } finally {
      if (fieldName === "banner") setBannerUploading(false);
      if (e?.target) e.target.value = "";
    }
  };

  const {
    data: productData,
    isLoading: isProductLoading,
    error: productError,
    refetch,
  } = useGetMyCreatorProductsQuery(
    userInfo?._id
      ? {
          pageNumber,
          keyword,
          category,
          age,
          sort,
          creatorCategoryId: selectedCreatorCategory,
        }
      : skipToken
  );

  const handleCreateProduct = async () => {
    try {
      const createdProduct = await createProductCreator().unwrap();
      navigate(`/profile/creator/${createdProduct._id}/edit`);
    } catch (error) {
      console.error("Error creating product:", error);
    }
  };

  const handleViewAsUser = () => {
    if (creatorInfo?.tag) navigate(`/user/creator/${creatorInfo.tag}`);
  };

  const handleAssignProductToCategory = async (productId, categoryId) => {
    try {
      const res = await addProductToCreatorCategory({ categoryId, productId }).unwrap();
      toast.success(res?.message || "Product added to category");
      refetchCreatorMeta();
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || "Failed to add product to category");
    }
  };

  const openCreateCategoryModal = (productId = null) => {
    setCategoryModalProductId(productId);
    setCategoryInputName("");
    setIsCategoryModalOpen(true);
  };

  const closeCreateCategoryModal = () => {
    setIsCategoryModalOpen(false);
    setCategoryInputName("");
    setCategoryModalProductId(null);
    setIsCategoryModalSubmitting(false);
  };

  const submitCreateCategoryModal = async () => {
    const trimmedName = categoryInputName.trim();
    if (!trimmedName) {
      toast.error("Category name is required");
      return;
    }

    setIsCategoryModalSubmitting(true);
    try {
      const created = await createCreatorProductCategory(trimmedName).unwrap();
      if (categoryModalProductId) {
        await addProductToCreatorCategory({
          categoryId: created?._id,
          productId: categoryModalProductId,
        }).unwrap();
        toast.success("Category created and product added");
        refetch();
      } else {
        toast.success("Category created");
      }
      refetchCreatorMeta();
      closeCreateCategoryModal();
    } catch (err) {
      toast.error(err?.data?.message || "Failed to create category");
      setIsCategoryModalSubmitting(false);
    }
  };

  const handleRemoveProductFromCategory = async (productId, categoryId) => {
    try {
      const res = await removeProductFromCreatorCategory({ categoryId, productId }).unwrap();
      toast.success(res?.message || "Product removed from category");
      refetchCreatorMeta();
      refetch();
    } catch (err) {
      toast.error(err?.data?.message || "Failed to remove product from category");
    }
  };

  if (creatorInfoLoading) return <Loader />;
  if (isUpdating) return <Loader />;
  if (notUpdating) {
    return <Message variant="danger">{notUpdating?.data?.message || notUpdating.error}</Message>;
  }

  const handlers = [
    { name: "Facebook", icon: <FaFacebook />, toggle: facebookToggle, setToggle: setFacebookToggle, value: facebook, setValue: setFacebook },
    { name: "Instagram", icon: <FaInstagram />, toggle: instagramToggle, setToggle: setInstagramToggle, value: instagram, setValue: setInstagram },
    { name: "Pinterest", icon: <FaPinterest />, toggle: pinterestToggle, setToggle: setPinterestToggle, value: pinterest, setValue: setPinterest },
    { name: "TikTok", icon: <FaTiktok />, toggle: tiktokToggle, setToggle: setTiktokToggle, value: tiktok, setValue: setTiktok },
  ];

  return (
    <div className="space-y-6">
      <button
        type="button"
        onClick={handleViewAsUser}
        className="inline-flex items-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
      >
        <FaUser className="mr-2" />
        View Page as User
      </button>

      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-4">
          {bannerImage && !bannerError ? (
            <img
              src={bannerImage}
              alt="Banner Preview"
              className="h-52 w-full rounded-xl object-cover"
              onError={() => setBannerError(true)}
            />
          ) : (
            <div className="flex h-52 w-full items-center justify-center rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 text-sm text-slate-500">
              No banner image uploaded
            </div>
          )}
        </div>
        <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 p-4">
          <label className="mb-1.5 block text-sm font-medium text-slate-700">Upload New Banner</label>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-600">
              <FaCheckCircle className={bannerImage && !bannerError ? "text-emerald-500" : "text-slate-400"} />
              {bannerStatus}
            </span>
          </div>
          <p className="mb-3 text-xs leading-5 text-slate-500">
            Your banner appears across the top of your public creator page. If you leave it empty,
            Practidex shows a fallback banner automatically.
          </p>
          <label className="flex cursor-pointer items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm transition hover:border-sky-300 hover:bg-sky-50">
            <span className="font-semibold text-slate-800">{bannerUploading ? "Uploading banner..." : "Choose banner image"}</span>
            <span className="rounded-full bg-sky-600 px-3 py-1.5 text-xs font-semibold text-white">
              Browse
            </span>
            <input
              type="file"
              name="banner"
              disabled={bannerUploading}
              accept="image/*"
              onChange={(e) => uploadFileHandler(e, "banner")}
              className="sr-only"
            />
          </label>
          {bannerUploading && (
            <div role="status" aria-live="polite" className="mt-3 flex items-center gap-2 text-sm text-sky-700">
              <span aria-hidden="true" className="h-5 w-5 animate-spin rounded-full border-2 border-sky-200 border-t-sky-600" />
              Uploading and checking your banner. Please wait...
            </div>
          )}
        </div>
      </div>

      <form onSubmit={submitHandler} className="space-y-5 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          <div className="space-y-3 lg:col-span-4">
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start lg:flex-col">
                {profileImage && !profileError ? (
                  <img
                    src={profileImage}
                    alt="Profile Preview"
                    className="h-32 w-32 shrink-0 rounded-full object-cover"
                    onError={() => setProfileError(true)}
                  />
                ) : (
                  <div className="flex h-32 w-32 shrink-0 items-center justify-center rounded-full border-2 border-dashed border-slate-300 bg-white text-xs text-slate-500">
                    No profile image uploaded
                  </div>
                )}

                <div className="min-w-0 flex-1">
                  <label className="mb-1.5 block text-sm font-medium text-slate-700">Upload Profile Image</label>
                  <div className="mb-3 flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-600">
                      <FaCheckCircle className={profileImage && !profileError ? "text-emerald-500" : "text-slate-400"} />
                      {profileStatus}
                    </span>
                  </div>
                  <p className="mb-3 text-xs leading-5 text-slate-500">
                    Your profile image appears on your creator page and product cards so buyers can
                    recognize your storefront quickly.
                  </p>
                  <label className="flex cursor-pointer items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 shadow-sm transition hover:border-sky-300 hover:bg-sky-50">
                    <span className="font-semibold text-slate-800">Choose profile image</span>
                    <span className="rounded-full bg-sky-600 px-3 py-1.5 text-xs font-semibold text-white">
                      Browse
                    </span>
                    <input
                      type="file"
                      name="profile"
                      accept="image/*"
                      onChange={(e) => uploadFileHandler(e, "profile")}
                      className="sr-only"
                    />
                  </label>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4 lg:col-span-8">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Name</label>
              <input
                type="text"
                placeholder="Enter name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Creator Tag</label>
              <input
                type="text"
                placeholder="Enter Creator Tag"
                value={tag}
                onChange={(e) => setTag(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
              />
            </div>

            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700">Description</label>
              <textarea
                rows={4}
                placeholder="Enter description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
              />
            </div>
          </div>
        </div>

        <section className="space-y-3">
          <h3 className="text-lg font-semibold text-slate-900">Social Handlers</h3>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {handlers.map((handler, idx) => (
              <div key={idx} className="rounded-xl border border-slate-200 bg-slate-50 p-3">
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-slate-800">
                  {handler.icon} {handler.name}
                </div>
                <label className="mb-2 flex items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    checked={handler.toggle}
                    onChange={(e) => handler.setToggle(e.target.checked)}
                    className="h-4 w-4 accent-sky-600"
                  />
                  Enable
                </label>
                <input
                  type="text"
                  placeholder={`Enter ${handler.name} link`}
                  value={handler.value}
                  onChange={(e) => handler.setValue(e.target.value)}
                  disabled={!handler.toggle}
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-400"
                />
              </div>
            ))}
          </div>
        </section>

        <button
          type="submit"
          disabled={bannerUploading}
          className="rounded-xl bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-sky-700"
        >
          Update Creator Data
        </button>
      </form>

      <section className="space-y-4">
        {isProductLoading ? (
          <Loader />
        ) : productError ? (
          <Message variant="danger">
            {productError?.productData?.message || productError.error}
          </Message>
        ) : (
          <>
            <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
              <div className="flex flex-col gap-4">
                <div>
                  <p className="mb-1 text-xs font-semibold uppercase tracking-[0.18em] text-sky-700">
                    Creator Workspace
                  </p>
                  <h2 className="text-2xl font-semibold text-slate-900">My Products</h2>
                  <p className="mt-1 text-sm leading-6 text-slate-500">
                    Search, sort, and organize your catalog from one place.
                  </p>
                </div>
                <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
                  <div className="min-w-0">
                    <SearchBoxCreator isCreator={userInfo.isCreator} />
                  </div>
                  <div className="flex flex-wrap gap-2 lg:justify-end">
                    <button
                      type="button"
                      onClick={handleCreateProduct}
                      disabled={isCreating}
                      className="rounded-xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                    >
                      {isCreating ? "Creating..." : "Create New Product"}
                    </button>
                  </div>
                </div>
              </div>

              <div className="mt-4 border-t border-slate-100 pt-4">
                <SearchHelper isCreator={userInfo.isCreator} compact />
              </div>
              <div className="mt-4 space-y-4 border-t border-slate-100 pt-4">
                <CreatorPaymentWarning user={userInfo} />

                <div className="grid grid-cols-1 gap-4 md:grid-cols-12">
                  <aside className="md:col-span-3">
                    <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                      <h3 className="mb-3 text-base font-semibold text-slate-900">Categories</h3>
                      <p className="mb-3 text-xs leading-5 text-slate-500">
                        Group products into custom sections for visitors browsing your creator page.
                      </p>
                      <button
                        type="button"
                        onClick={() => openCreateCategoryModal()}
                        className="mb-3 w-full rounded-2xl bg-gradient-to-r from-sky-600 via-cyan-500 to-emerald-500 px-4 py-3 text-left text-sm font-semibold text-white shadow-sm transition hover:shadow-md"
                      >
                        <span className="block">Create New Category</span>
                        <span className="mt-1 block text-xs font-medium text-white/80">
                          Add a themed section for your catalog
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => setSelectedCreatorCategory("all")}
                        className={`mb-2 w-full rounded-xl px-3 py-2.5 text-left text-sm font-semibold leading-5 transition ${
                          selectedCreatorCategory === "all"
                            ? "bg-sky-600 text-white"
                            : "bg-white text-slate-700 hover:bg-slate-100"
                        }`}
                      >
                        <span className="block break-words whitespace-normal">
                          All Resources ({creatorCategoryData?.allResourcesCount ?? productData?.count ?? 0})
                        </span>
                      </button>
                      {(creatorCategories || []).map((entry) => (
                        <button
                          key={entry._id}
                          type="button"
                          onClick={() => setSelectedCreatorCategory(String(entry._id))}
                          className={`mb-2 w-full rounded-xl px-3 py-2.5 text-left text-sm font-semibold leading-5 transition ${
                            selectedCreatorCategory === String(entry._id)
                              ? "bg-sky-600 text-white"
                              : "bg-white text-slate-700 hover:bg-slate-100"
                          }`}
                        >
                          <span className="block break-words whitespace-normal">
                            {entry.name} ({entry.count ?? 0})
                          </span>
                        </button>
                      ))}
                    </div>
                  </aside>

                  <div className="md:col-span-9">
                    {productData?.products?.length > 0 ? (
                      <div className="grid grid-cols-1 justify-items-center gap-4 sm:grid-cols-2 xl:grid-cols-2 2xl:grid-cols-3 xl:justify-items-start">
                        {productData.products.map((product) => (
                          <Product
                            key={product._id}
                            product={product}
                            isCreator={userInfo.isCreator}
                            creatorCategories={creatorCategories}
                            onAssignToCategory={handleAssignProductToCategory}
                            onRemoveFromCategory={handleRemoveProductFromCategory}
                            onCreateCategoryAndAssign={openCreateCategoryModal}
                            className="w-full max-w-[19.5rem]"
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 text-center">
                        <h3 className="text-lg font-semibold text-slate-900">No items were found</h3>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <Paginate
              pages={productData.pages}
              page={productData.page}
              keyword={keyword || ""}
              age={age || ""}
              sort={sort || ""}
              isCreator={userInfo.isCreator}
            />
          </>
        )}
      </section>

      {isCategoryModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
          onClick={closeCreateCategoryModal}
          role="presentation"
        >
          <div
            className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Create category"
          >
            <h3 className="text-lg font-semibold text-slate-900">Create New Category</h3>
            <p className="mt-1 text-sm text-slate-600">
              Enter a name for the new category.
            </p>
            <input
              type="text"
              value={categoryInputName}
              onChange={(e) => setCategoryInputName(e.target.value)}
              placeholder="Category name"
              className="mt-4 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm text-slate-800 outline-none focus:border-sky-300 focus:ring-2 focus:ring-sky-100"
              maxLength={60}
              autoFocus
            />
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={closeCreateCategoryModal}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitCreateCategoryModal}
                disabled={isCategoryModalSubmitting}
                className="rounded-xl bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                {isCategoryModalSubmitting ? "Creating..." : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default CreatorEditScreen;

